import React from 'react';
import {
    BATCH_PROCESSING_ASSIGNMENT_GROUP,
    BATCH_PROCESSING_WAIT_HOURS,
    getBatchProcessingWaitInfo,
} from 'utilities/batchProcessing';

interface IProps {
    /** The raw rateErrorMessage returned by the details API (SAP error text). */
    rateErrorMessage?: string | null;
    /** Timestamp (UTC) of the last Sales Order request sent to SAP. */
    lastSORequestSentAt?: string | null;
}

interface IState {
    /** Local tick counter used to force a re-render so the countdown stays live. */
    tick: number;
    dismissed: boolean;
}

// Refresh the remaining-time countdown once a minute so the user does not have
// to reload the page to see it decrease. On a full page refresh the component
// remounts and recomputes from the current time as well.
const REFRESH_INTERVAL_MS = 60 * 1000;

export class BatchProcessingWarning extends React.Component<IProps, IState> {
    private timer: ReturnType<typeof setInterval> | undefined;

    constructor(props: IProps) {
        super(props);
        this.state = { tick: 0, dismissed: false };
    }

    public componentDidMount() {
        this.timer = setInterval(() => {
            this.setState((prev) => ({ tick: prev.tick + 1 }));
        }, REFRESH_INTERVAL_MS);
    }

    public componentWillUnmount() {
        this.stopTimer();
    }

    public render() {
        if (this.state.dismissed) {
            return null;
        }
        const waitInfo = getBatchProcessingWaitInfo(this.props.lastSORequestSentAt);
        return (
            <div className="message warning batch-processing-warning">
                <i aria-hidden="true" className="fas fa-exclamation-triangle" />
                <div>{this.renderMessage(waitInfo)}</div>
                <button
                    type="button"
                    id="close_batch_processing_warning"
                    className="batch-processing-warning__close"
                    aria-label="Dismiss warning"
                    onClick={this.handleDismiss}
                >
                    <i aria-hidden="true" className="fas fa-times" />
                </button>
            </div>
        );
    }

    private stopTimer = () => {
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = undefined;
        }
    };

    // Stop the per-minute refresh once dismissed so we don't keep re-rendering
    // a hidden (null) banner for the rest of the session.
    private handleDismiss = () => {
        this.stopTimer();
        this.setState({ dismissed: true });
    };

    private renderMessage = (
        waitInfo: ReturnType<typeof getBatchProcessingWaitInfo>,
    ): React.ReactNode => {
        // The last request timestamp is unknown: we cannot compute a countdown,
        // so fall back to the fixed wait window guidance.
        if (!waitInfo.hasTimestamp) {
            return (
                <>
                    <span>
                        This Sales Order request is still being processed by SAP.
                        A successful acknowledgement may still arrive, so please
                        allow up to {BATCH_PROCESSING_WAIT_HOURS} hours from the
                        last Sales Order request before raising an incident.
                    </span>
                </>
            );
        }

        // Wait window has elapsed: the user may now raise an incident.
        if (waitInfo.canRaiseIncident) {
            return (
                <>
                    <span>
                        More than {BATCH_PROCESSING_WAIT_HOURS} hours have passed
                        since the last Sales Order request
                        ({waitInfo.lastRequestedAt} UTC) without a successful
                        acknowledgement from SAP. You can now raise an incident
                        under the assignment group
                        '{BATCH_PROCESSING_ASSIGNMENT_GROUP}'.
                    </span>
                    {this.props.rateErrorMessage && (
                        <span className="batch-processing-warning__details">
                            Error details: {this.props.rateErrorMessage}
                        </span>
                    )}
                </>
            );
        }

        // Still within the wait window: show how much time is left.
        return (
            <>
                <span>
                    This Sales Order request is still being processed by SAP. A
                    successful acknowledgement may still arrive automatically.
                </span>
                <span>
                    Please wait approximately <b>{waitInfo.remainingLabel}</b>
                    {' '}(until {waitInfo.deadline} UTC) before raising an
                    incident.
                </span>
            </>
        );
    };
}

export default BatchProcessingWarning;
